import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LogOut, Package, Settings, Truck, Users } from 'lucide-react';
import { getInitials, getName } from '../utils/userUtils';
import { hasUserPermission, PERMISSIONS } from '../utils/permissionUtils';
import AppContext from '../contexts/AppContext';
import LanguageSwitcher from './LanguageSwitcher';
import ImpersonationBanner from './ImpersonationBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const AdminHeader = ({ username, onLogout, email }) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AppContext);
  const canViewQuotas = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_QUOTAS_VIEW);
  const canViewStocks = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_STOCKS_VIEW);
  const canViewDeployments = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_DEPLOYMENTS_VIEW);

  const handleLogout = () => {
    onLogout();
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black h-[80px] flex items-center border-b border-gray-800">
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/home">
              <img
                src="/logo_gps_white.png"
                alt="Globe Perfect Store Admin"
                className="w-[120px] h-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/admin"
                className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 rounded-md transition-colors"
              >
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">{t('Users')}</span>
              </Link>
              {canViewQuotas && (
                <Link
                  to="/admin/quotas"
                  className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 rounded-md transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('Quotas')}</span>
                </Link>
              )}
              {canViewStocks && (
                <Link
                  to="/admin/stocks"
                  className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 rounded-md transition-colors"
                >
                  <Package className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('Stocks')}</span>
                </Link>
              )}
              {canViewDeployments && (
                <Link
                  to="/admin/deployments"
                  className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 rounded-md transition-colors"
                >
                  <Truck className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('Deployments')}</span>
                </Link>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <ImpersonationBanner />
            <LanguageSwitcher />
            {username && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none">
                    <span className="text-white text-sm font-medium">
                      {getName(username)}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                      <span className="text-white text-xs font-medium">
                        {getInitials(username)}
                      </span>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{getName(username)}</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        {email || username}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('Log out')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

AdminHeader.propTypes = {
  username: PropTypes.string.isRequired,
  onLogout: PropTypes.func.isRequired,
  email: PropTypes.string,
};

export { AdminHeader };
